import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { getScrollParent } from '@shared/modules/detail-view/common/helpers/get-scroll-parent';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { SettingsScope } from '@shared/modules/auth/settings/common/settings-scope';
import {
  ConversationSettingsScopeProperty,
  MailCommentAttachmentPosition
} from '@shared/modules/auth/settings/common/conversation-settings-scope';
import { EnumObjectValue } from '@shared/utils/common/types';
import { BaseMailCommentComponent } from '@shared/modules/comments/components/mail-comment/mail-comment-base';
import {
  AdditionalTranslateFromLanguage,
  TranslateResponse
} from '@shared/modules/smart-commands/common/translations/interfaces/responses';
import { ToastService } from '@shared/ui/kit/toast/toast.service';
import { OpenAiOperationsService } from '@shared/modules/open-ai/components/services/open-ai-operations/open-ai-operations.service';
import { CustomDropdownPlacement } from '@shared/ui/dropdown/custom-dropdown/custom-dropdown.service';
import { ChannelType } from '@shared/modules/comments/common/constants/channel-type';
import { MailCommentOperationsService } from '@shared/modules/comments/components/mail-comment/mail-comment-operations/mail-comment-operations.service';
import { isConversationClosed } from '@shared/modules/conversations/common/helpers/is-conversation-closed';
import { getAllowedImpersonatedSenders } from '@shared/modules/contacts/common/helpers/get-allowed-impersonated-senders';
import { IS_EXTERNAL_WINDOW } from '@shared/ui/kit/dialog/common/injection-tokens/is-external-window';
import { TrackingService } from '@shared/modules/tracking/tracking.service';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, share, Subscription, switchMap } from 'rxjs';
import { CommentMailModel, MimeType } from '@dta/shared/models-api-loop/comment/comment.model';
import { catchError, filter, map, publishReplay, refCount, skip, take } from 'rxjs/operators';
import { ConversationModel } from '@dta/shared/models-api-loop/conversation-card/conversation/conversation.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { KeyboardActionType } from '@dta/shared/models/keyboard-shortcut.model';
import { KeyboardShortcutsListenerService } from '@dta/ui/services/keyboard-shortcuts-listener/keyboard-shortcuts-listener.service';
import { BBCodeService } from '@dta/shared/services/bbcode/bbcode.service';

@UntilDestroy()
@Component({
  selector: 'loop-mail-comment',
  templateUrl: './mail-comment.component.html',
  styleUrls: ['./mail-comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MailCommentComponent extends BaseMailCommentComponent implements OnInit, OnDestroy {
  private readonly injector: Injector = inject(Injector);
  readonly elementRef: ElementRef = inject(ElementRef);
  private readonly keyboardShortcutsListenerService: KeyboardShortcutsListenerService = inject(
    KeyboardShortcutsListenerService
  );
  protected readonly bbCodeService: BBCodeService = inject(BBCodeService);
  private readonly mailCommentOperationsService: MailCommentOperationsService = inject(MailCommentOperationsService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly trackingService: TrackingService = inject(TrackingService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly openAiOperationsService: OpenAiOperationsService = inject(OpenAiOperationsService);
  protected readonly isExternalWindow: boolean | null = inject(IS_EXTERNAL_WINDOW, { optional: true });

  protected get isClosed(): boolean {
    return isConversationClosed(this.conversation);
  }

  protected readonly isAIEnabledSignal: Signal<boolean> = toSignal(
    this.userAuthService.currentSession$.pipe(map(session => session.license.aiEnabled)),
    { initialValue: false }
  );

  protected readonly translatedCommentResponse$: BehaviorSubject<{
    response: TranslateResponse;
    isOriginal: boolean;
  } | null> = new BehaviorSubject<{ response: TranslateResponse; isOriginal: boolean } | null>(null);
  protected readonly comment$: ReplaySubject<CommentMailModel> = new ReplaySubject<CommentMailModel>(1);
  @Input() set comment(comment: CommentMailModel) {
    this.comment$.next(comment);
  }
  @Input() shouldAppendCollapse: boolean = true;
  @Input() conversation?: ConversationModel;
  @Output() openQuickReply: EventEmitter<CommentMailModel> = new EventEmitter<CommentMailModel>();
  @Output() closeQuickReply: EventEmitter<void> = new EventEmitter<void>();
  readonly isCompactSignal: Signal<boolean> = input(false, { alias: 'isCompact' });

  @ViewChild('dropdownContainerTpl', { read: ViewContainerRef }) private dropdownViewContainerRef: ViewContainerRef;

  protected readonly ChannelType_LoopSpam: typeof ChannelType.loopSpam = ChannelType.loopSpam;

  protected readonly attachmentPosition$: Observable<EnumObjectValue<typeof MailCommentAttachmentPosition>> =
    this.userAuthService.currentSession$.pipe(
      map(
        session =>
          (session?.userConfiguration?.[SettingsScope.conversation]?.[
            ConversationSettingsScopeProperty.mail_comment_attachment_position
          ] ?? MailCommentAttachmentPosition.bottom) as EnumObjectValue<typeof MailCommentAttachmentPosition>
      ),
      publishReplay(1),
      refCount()
    );

  protected readonly areAttachmentsPreviewsShown$: Observable<boolean> = this.userAuthService.currentSession$.pipe(
    map(
      session =>
        !!session?.userConfiguration?.[SettingsScope.conversation]?.[
          ConversationSettingsScopeProperty.are_attachments_previews_shown
        ]
    ),
    publishReplay(1),
    refCount()
  );

  private scrollTopSubscription?: Subscription;
  private mailCommentFullyLoadedEmissionCount: number = 0;

  protected readonly commentHtml$: Observable<string> = combineLatest([
    this.comment$,
    this.translatedCommentResponse$
  ]).pipe(
    switchMap(([comment, translatedCommentResponse]) => {
      if (translatedCommentResponse && !translatedCommentResponse.isOriginal) {
        return of(translatedCommentResponse.response.translated_text).pipe(
          switchMap(body => {
            return this.mailCommentOperationsService.replaceImgCidWIthFilePaths(comment, body);
          })
        );
      }

      if (comment.body.mimeType === MimeType.bbtag && typeof comment.body?.content === 'string') {
        return of(this.bbCodeService.process({ text: comment.body.content }));
      }

      return this.mailCommentOperationsService.getCommentHtml$(comment);
    }),
    share({
      connector: () => new ReplaySubject(1),
      resetOnRefCountZero: false
    })
  );

  protected readonly fromOptionsCount$: Observable<number> = getAllowedImpersonatedSenders(this.injector).pipe(
    map(senders => senders.length),
    publishReplay(1),
    refCount()
  );

  ngOnInit(): void {
    this.comment$
      .pipe(
        filter(comment => comment.isLastMailComment),
        switchMap(comment => {
          return this.keyboardShortcutsListenerService
            .getActionsByTypes([KeyboardActionType.REPLY, KeyboardActionType.REPLY_TO_ALL, KeyboardActionType.FORWARD])
            .pipe(
              map(action => {
                return {
                  action: action,
                  comment: comment
                };
              })
            );
        }),
        untilDestroyed(this)
      )
      .subscribe(({ action, comment }) => {
        if (!action || this.isClosed || !comment.isLastMailComment) {
          return;
        }

        switch (action.action.type) {
          case KeyboardActionType.REPLY_TO_ALL:
            this.replyAll(comment);
            break;
          case KeyboardActionType.REPLY:
            this.reply(comment);
            break;
          case KeyboardActionType.FORWARD:
            this.forward(comment);
            break;
        }
      });
  }

  protected handlePopOut(comment: CommentMailModel): void {
    if (this.isExternalWindow || !this.conversation) {
      return;
    }

    this.mailCommentOperationsService
      .openCommentInNewWindow$(this.conversation.name, comment.id)
      .pipe(take(1), untilDestroyed(this))
      .subscribe();
  }

  protected handleOpenQuickReply(comment: CommentMailModel): void {
    if (!this.isExternalWindow) {
      this.openQuickReply.emit(comment);
      return;
    }

    this.replyAll(comment);
  }

  commentFullyLoaded(): void {
    this.mailCommentFullyLoadedEmissionCount++;
    this.mailCommentFullyLoaded$.next();
  }

  protected handleCommentMailTranslate(translateResponse: TranslateResponse): void {
    this.translatedCommentResponse$.next({
      response: translateResponse,
      isOriginal: false
    });
  }

  protected handleLanguageChange({
    from,
    to,
    isOriginal
  }: {
    from?: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>;
    to?: string;
    isOriginal?: boolean;
  }): void {
    let currentTranslatedResponse = this.translatedCommentResponse$.getValue();

    if (from) {
      currentTranslatedResponse = {
        ...currentTranslatedResponse,
        isOriginal: false,
        response: {
          ...currentTranslatedResponse.response,
          from_language: from
        }
      };
    }

    if (to) {
      currentTranslatedResponse = {
        ...currentTranslatedResponse,
        isOriginal: false,
        response: {
          ...currentTranslatedResponse.response,
          to_language: to
        }
      };
    }

    if (!from && !to && isOriginal) {
      this.translatedCommentResponse$.next({
        ...currentTranslatedResponse,
        isOriginal: isOriginal
      });

      return;
    }

    const fromLanguage =
      currentTranslatedResponse.response.from_language !== AdditionalTranslateFromLanguage.detect
        ? currentTranslatedResponse.response.from_language
        : undefined;

    this.comment$
      .pipe(
        take(1),
        switchMap(comment => {
          return this.mailCommentOperationsService.translateCommentMail({
            conversation: this.conversation,
            replyToComment: comment,
            to_language: currentTranslatedResponse.response.to_language,
            from_language: fromLanguage
          });
        }),
        untilDestroyed(this),
        take(1),
        catchError(() => {
          this.toastService.info({ title: 'There was an error translating your message' });
          return of(undefined);
        })
      )
      .subscribe(translateResponse => {
        if (!translateResponse) {
          return;
        }

        if (currentTranslatedResponse.response.from_language === AdditionalTranslateFromLanguage.detect) {
          translateResponse = {
            ...translateResponse,
            from_language: AdditionalTranslateFromLanguage.detect
          };
        }
        this.handleCommentMailTranslate(translateResponse);
      });
  }

  toggleCollapse(): void {
    if (this.isExternalWindow) {
      return;
    }

    const parent = getScrollParent(this.elementRef.nativeElement);

    this.isCollapsed = !this.isCollapsed;

    this.trackingService.trackUserClick('MailComment', 'Toggle collapse', {
      conversationId: this.conversation?.cardId,
      isCollapsed: this.isCollapsed
    });

    // always force scroll top to remain the same
    const currentScrollTop = parent?.scrollTop;
    if (parent) {
      this.scrollTopSubscription?.unsubscribe();
      // mailCommentFullyLoaded$ is an unbounded ReplaySubject, so a fresh subscription would
      // otherwise replay stale emissions from before this toggle instead of waiting for the
      // newly (re)rendered content to finish loading.
      this.scrollTopSubscription = this.mailCommentFullyLoaded$
        .pipe(skip(this.mailCommentFullyLoadedEmissionCount), take(1), untilDestroyed(this))
        .subscribe(() => {
          parent.scrollTop = currentScrollTop;
        });
    }

    if (currentScrollTop && parent) {
      parent.scrollTop = currentScrollTop;
    }
  }

  replyAll(comment: CommentMailModel): void {
    this.mailCommentOperationsService.replyAction({
      conversation: this.conversation,
      replyToComment: comment,
      groupId: this.conversation?.findGroupInShareList()?.id,
      replyType: 'replyAll',
      closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
      trackingAction: 'ReplyAll'
    });
  }

  reply(comment: CommentMailModel): void {
    this.mailCommentOperationsService.replyAction({
      conversation: this.conversation,
      replyToComment: comment,
      groupId: this.conversation?.findGroupInShareList()?.id,
      replyType: 'replyOne',
      closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
      trackingAction: 'Reply'
    });
  }

  forward(comment: CommentMailModel): void {
    this.mailCommentOperationsService.forwardAction({
      conversation: this.conversation,
      replyToComment: comment,
      groupId: this.conversation?.findGroupInShareList()?.id,
      closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
      trackingAction: 'Forward'
    });
  }

  protected openOpenAiDropdownOptions(previousComment: CommentMailModel): void {
    getAllowedImpersonatedSenders(this.injector)
      .pipe(take(1))
      .subscribe(sendAsOptions => {
        this.openAiOperationsService.openOpenAiDropdownOptions({
          viewContainerRef: this.dropdownViewContainerRef,
          onContentAccept: content => {
            this.mailCommentOperationsService.replyAction({
              conversation: this.conversation,
              replyToComment: previousComment,
              groupId: this.conversation?.findGroupInShareList()?.id,
              replyType: 'replyAll',
              content: content.replace(/\n/g, '<br>')
            });
          },
          previousContent: previousComment.body.content,
          dropdownPosition: CustomDropdownPlacement.top_end,
          openAiContentButtonOptions: {
            isCloseButtonVisible: false,
            closeButtonText: '',
            okButtonText: 'Open in reply'
          },
          isReplyToSelf: sendAsOptions?.some(sendAsOption => sendAsOption?.email === previousComment.author?.email),
          trackingLocation: 'MailComment',
          conversation: this.conversation,
          injector: this.injector
        });
      });
  }

  ngOnDestroy(): void {
    this.comment$.complete();
    this.translatedCommentResponse$.complete();
  }
}
